import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../../../hooks/useTheme';
import { LoginCredentials } from '../types';
import { Card, Typography, Form, Input, Button, Space, Checkbox, Divider, Alert, notification, Flex, Layout } from 'antd';
import { EyeInvisibleOutlined, GoogleOutlined } from '@ant-design/icons';
import './Login.css';

// Import assets
import loaderSrc from '../../../assets/icons/loader.svg';

const { Title, Link } = Typography;
const { Footer } = Layout;

export function Login() {
  const [credentials] = useState<LoginCredentials>({
    email: '',
    password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const { login, error, user } = useAuth();
  const { themeName } = useTheme();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [showLoginError, setShowLoginError] = useState(false);

  const handleSubmit = async (values: LoginCredentials) => {
    setSubmitting(true);
    setShowLoginError(false);
    try {
      const success = await login(values);
      if (success) {
        // Clear all persistent notifications on successful login
        notification.destroy();
        
        // Navigate to account root - SmartRedirect will handle role-based routing
        // We get the user from localStorage since the context hasn't updated yet
        const token = localStorage.getItem('accessToken');
        if (token) {
          try {
            // Decode the token to get user info immediately
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const userData = JSON.parse(jsonPayload);
            navigate(`/accounts/${userData.accountId}`);
          } catch (decodeError) {
            // Fallback: navigate to account root and let SmartRedirect handle it
            navigate(`/accounts/${user?.accountId}`);
          }
        }
      } else {
        setShowLoginError(true);
      }
    } catch (err) {
      setShowLoginError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecovery = () => {
    const email = form.getFieldValue('email');
    navigate('/recoveraccount', { state: { email } });
  };

  const toggleErrorAlert = () => {
    setShowLoginError(false);
  };

  return (
    <div className="sign-in-outer-container">
      <Flex className="sign-in-layout" justify="space-around" align="center">
        {/* Left side - Logo and Welcome Text */}
        <div className="sign-in-left-section">
          <div className="sign-in-brand-section">
            <div className="sign-in-logo-img" />
            <img src={loaderSrc} alt="Loader" className="sign-in-loader" />
            <Title level={1} className="sign-in-welcome-text">
              Welcome <br /> aboard.
            </Title>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="sign-in-right-section">
          <Card className="sign-in-card-container">
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              <div className="sign-in-form-header">
                <Title level={2} className="sign-in-title">
                  Log in to your account
                </Title>
              </div>

              {showLoginError && (
                <Alert
                  message={error || "Your email or password is incorrect. Please try again!"}
                  type="error"
                  className="sign-in-alert"
                  onClose={toggleErrorAlert}
                  showIcon
                  closable
                />
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={credentials}
                requiredMark={false}
                size="large"
              >
                <Form.Item
                  label="Email address"
                  name="email"
                  className="sign-in-form-item"
                  rules={[{ type: 'email', required: true, message: 'Please enter a valid email address!' }]}
                >
                  <Input 
                    autoComplete="email" 
                    className="sign-in-input"
                  />
                </Form.Item>

                <Form.Item
                  label="Password"
                  name="password"
                  className="sign-in-form-item"
                  rules={[{ required: true, message: 'Please enter your password!' }]}
                >
                  <Input.Password
                    autoComplete="current-password"
                    iconRender={(visible) => (visible ? <EyeInvisibleOutlined /> : <EyeInvisibleOutlined />)}
                    className="sign-in-input"
                  />
                </Form.Item>

                <Form.Item className="sign-in-form-item-bottom">
                  <Flex justify="end" align="center">
                   {/*  <Checkbox className="sign-in-checkbox" id="remember-me-checkbox">
                      Remember me
                    </Checkbox> */}
                    <Link onClick={handleRecovery} className="sign-in-link" id="forgot-password-link">
                      Forgot password?
                    </Link>
                  </Flex>
                </Form.Item>

                <Form.Item className="sign-in-form-item-no-margin">
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={submitting}
                    size="large"
                    className="sign-in-btn"
                    id="login-submit-button"
                  >
                    {submitting ? 'Signing in...' : 'Login'}
                  </Button>
                </Form.Item>
              </Form>

{/*           {!showLoginError && (
            <>
              <Divider plain className="sign-in-divider">or</Divider>
              <Button icon={<GoogleOutlined />} block size="large" id="google-signin-button">
                Sign in with Google
              </Button>
            </>
          )} */}
            </Space>
          </Card>
        </div>
      </Flex>
      
      
        <Footer className="sign-in-footer">
          <span>Born from the spirit of discovery</span>
          {themeName !== 'neutral' && (
          <span>{getComputedStyle(document.documentElement).getPropertyValue('--ant-appName').trim()}.com</span>
          )}
        </Footer>
     
    </div>
  );
} 